import React, { useState, useEffect, useCallback } from 'react';
import { courtApi, facilityApi } from '../api';
import { Court, CreateCourtData } from '../api_types';
import { PlusIcon, PencilIcon, TrashIcon, ChevronUpIcon, ChevronDownIcon, FunnelIcon, ClockIcon } from '@heroicons/react/24/outline';
import './styles/CourtManagement.css';
import LoadingSpinner from '../shared/components/LoadingSpinner';

interface CourtManagementProps {
  facilityId: string;
  businessId: string;
}

interface SortConfig {
  key: keyof Court;
  direction: 'asc' | 'desc';
}

interface FilterConfig {
  name: string;
  type: string;
  surface: string;
  hasLights: string;
}

interface BusinessHours {
  [key: string]: {
    isClosed: boolean;
    openTime: string;
    closeTime: string;
  };
}

const DAYS_OF_WEEK = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday'
];

const defaultBusinessHours: BusinessHours = {
  sunday: { isClosed: false, openTime: '07:00', closeTime: '21:00' },
  monday: { isClosed: false, openTime: '07:00', closeTime: '21:00' },
  tuesday: { isClosed: false, openTime: '07:00', closeTime: '21:00' },
  wednesday: { isClosed: false, openTime: '07:00', closeTime: '21:00' },
  thursday: { isClosed: false, openTime: '07:00', closeTime: '21:00' },
  friday: { isClosed: false, openTime: '07:00', closeTime: '21:00' },
  saturday: { isClosed: false, openTime: '07:00', closeTime: '21:00' }
};

const formatTimeForDisplay = (time: string) => {
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours);
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
  return `${displayHour}:${minutes} ${period}`;
};

const formatTimeForInput = (time: string) => {
  const [timePart, period] = time.split(' ');
  const [hours, minutes] = timePart.split(':');
  let hour = parseInt(hours);
  if (period === 'PM' && hour < 12) hour += 12;
  if (period === 'AM' && hour === 12) hour = 0;
  return `${hour.toString().padStart(2, '0')}:${minutes}`;
};

const generateTimeOptions = () => {
  const options = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      options.push(time);
    }
  }
  return options;
};

const CourtManagement: React.FC<CourtManagementProps> = ({ facilityId, businessId }) => {
  const [courts, setCourts] = useState<Court[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingCourt, setEditingCourt] = useState<Court | null>(null);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'name', direction: 'asc' });
  const [filterConfig, setFilterConfig] = useState<FilterConfig>({
    name: '',
    type: '',
    surface: '',
    hasLights: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [showBusinessHoursModal, setShowBusinessHoursModal] = useState(false);
  const [businessHours, setBusinessHours] = useState<BusinessHours>(defaultBusinessHours);
  const [newCourt, setNewCourt] = useState<CreateCourtData>({
    name: '',
    type: 'tennis',
    surface: 'hard',
    hasLights: false
  });

  const surfaceTypes = [
    'hard',
    'clay',
    'grass',
    'artificial grass',
    'carpet',
    'other'
  ];

  const loadCourts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await courtApi.getCourts(businessId, facilityId);
      setCourts(data);
      setError(null);
    } catch (err) {
      setError('Failed to load courts');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [facilityId, businessId]);

  const loadBusinessHours = useCallback(async () => {
    try {
      const facility = await facilityApi.getFacility(facilityId, businessId);
      if (facility?.business_hours) {
        // Convert stored business hours to new format
        const convertedHours: BusinessHours = { ...defaultBusinessHours };
        Object.entries(facility.business_hours).forEach(([day, hours]) => {
          if (typeof hours === 'string') {
            if (hours === 'Closed') {
              convertedHours[day as keyof BusinessHours] = { isClosed: true, openTime: '07:00', closeTime: '21:00' };
            } else {
              const [openTime, closeTime] = hours.split(' - ').map(t => formatTimeForInput(t));
              convertedHours[day as keyof BusinessHours] = { isClosed: false, openTime, closeTime };
            }
          }
        });
        setBusinessHours(convertedHours);
      } else {
        setBusinessHours(defaultBusinessHours);
      }
    } catch (err) {
      console.error('Error loading business hours:', err);
      setBusinessHours(defaultBusinessHours);
    }
  }, [facilityId, businessId]);

  const handleCreateCourt = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await courtApi.createCourt(businessId, facilityId, newCourt);
      setNewCourt({
        name: '',
        type: 'tennis',
        surface: 'hard',
        hasLights: false
      });
      setShowModal(false);
      loadCourts();
    } catch (err) {
      setError('Failed to create court');
      console.error(err);
    }
  };

  const handleUpdateCourt = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingCourt) return;

    try {
      await courtApi.updateCourt(businessId, facilityId, editingCourt.id, editingCourt);
      setEditingCourt(null);
      loadCourts();
    } catch (err) {
      setError('Failed to update court');
      console.error(err);
    }
  };

  const handleDeleteCourt = async (courtId: string) => {
    if (window.confirm('Are you sure you want to delete this court?')) {
      try {
        await courtApi.deleteCourt(businessId, facilityId, courtId);
        loadCourts();
      } catch (err) {
        setError('Failed to delete court');
        console.error(err);
      }
    }
  };

  const handleSort = (key: keyof Court) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleFilterChange = (key: keyof FilterConfig, value: string) => {
    setFilterConfig(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const getSortedAndFilteredCourts = () => {
    let filteredCourts = [...courts];

    // Apply filters
    if (filterConfig.name) {
      filteredCourts = filteredCourts.filter(court => 
        court.name.toLowerCase().includes(filterConfig.name.toLowerCase())
      );
    }
    if (filterConfig.type) {
      filteredCourts = filteredCourts.filter(court => 
        court.type === filterConfig.type
      );
    }
    if (filterConfig.surface) {
      filteredCourts = filteredCourts.filter(court => 
        court.surface === filterConfig.surface
      );
    }
    if (filterConfig.hasLights !== '') {
      filteredCourts = filteredCourts.filter(court => 
        court.hasLights === (filterConfig.hasLights === 'true')
      );
    }

    // Apply sorting
    return filteredCourts.sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const getSortIcon = (key: keyof Court) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? 
      <ChevronUpIcon className="h-4 w-4 ml-1" /> : 
      <ChevronDownIcon className="h-4 w-4 ml-1" />;
  };

  const handleSaveBusinessHours = async () => {
    try {
      const facility = await facilityApi.getFacility(facilityId, businessId);
      if (facility) {
        // Convert back to string format for storage
        const hoursToSave: Record<string, string> = {};
        Object.entries(businessHours).forEach(([day, hours]) => {
          if (hours.isClosed) {
            hoursToSave[day] = 'Closed';
          } else {
            hoursToSave[day] = `${formatTimeForDisplay(hours.openTime)} - ${formatTimeForDisplay(hours.closeTime)}`;
          }
        });

        await facilityApi.updateFacility(facilityId, {
          ...facility,
          business_hours: hoursToSave
        }, businessId);
        setShowBusinessHoursModal(false);
      }
    } catch (err) {
      setError('Failed to save business hours');
      console.error(err);
    }
  };

  useEffect(() => {
    loadCourts();
    loadBusinessHours();
  }, [loadCourts, loadBusinessHours]);

  if (loading) {
    return (
      <div className="court-management-container">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="court-management-container">
        <div className="error-alert">
          <p className="error-alert-text">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col h-full min-h-0 w-full">
      {/* Sticky Header */}
      <div className="sticky top-0 z-10 bg-white shadow-sm border-b flex items-center justify-between px-8 py-5 w-full">
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">Court Management</h2>
        <div className="flex space-x-4">
          <button
            type="button"
            className="inline-flex items-center px-5 py-2.5 bg-indigo-600 text-white font-semibold rounded-md shadow-sm hover:bg-indigo-700 transition"
            onClick={() => setShowModal(true)}
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add New Court
          </button>
          <button
            type="button"
            className="inline-flex items-center px-5 py-2.5 bg-white border border-indigo-200 text-indigo-700 font-semibold rounded-md shadow-sm hover:bg-indigo-50 transition"
            onClick={() => setShowBusinessHoursModal(true)}
          >
            <ClockIcon className="h-5 w-5 mr-2" />
            Business Hours
          </button>
          <button
            type="button"
            className="inline-flex items-center px-5 py-2.5 bg-white border border-gray-200 text-gray-700 font-semibold rounded-md shadow-sm hover:bg-gray-50 transition"
            onClick={() => setShowFilters(!showFilters)}
          >
            <FunnelIcon className="h-5 w-5 mr-2" />
            Filters
          </button>
        </div>
      </div>

      <div className="flex-1 flex flex-col w-full">
        <div className="flex-1 flex flex-col bg-white shadow rounded-b-xl p-[3vw] min-h-0 w-full max-w-none text-[1.1vw]">
          {showFilters && (
            <div className="bg-white rounded-2xl shadow max-w-6xl mx-auto px-8 py-6 mb-8">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    className="w-full rounded-md border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 py-2 px-3 bg-white"
                    value={filterConfig.name}
                    onChange={(e) => handleFilterChange('name', e.target.value)}
                    placeholder="Filter by name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select
                    className="w-full rounded-md border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 py-2 px-3 bg-white"
                    value={filterConfig.type}
                    onChange={(e) => handleFilterChange('type', e.target.value)}
                  >
                    <option value="">All Types</option>
                    <option value="tennis">Tennis</option>
                    <option value="pickleball">Pickleball</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Surface</label>
                  <select
                    className="w-full rounded-md border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 py-2 px-3 bg-white"
                    value={filterConfig.surface}
                    onChange={(e) => handleFilterChange('surface', e.target.value)}
                  >
                    <option value="">All Surfaces</option>
                    {surfaceTypes.map(type => (
                      <option key={type} value={type}>
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Lights</label>
                  <select
                    className="w-full rounded-md border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 py-2 px-3 bg-white"
                    value={filterConfig.hasLights}
                    onChange={(e) => handleFilterChange('hasLights', e.target.value)}
                  >
                    <option value="">All</option>
                    <option value="true">With Lights</option>
                    <option value="false">Without Lights</option>
                  </select>
                </div>
              </div>
            </div>
          )}
          <div className="flex-1 overflow-x-auto overflow-y-auto rounded-lg min-h-0 w-full">
            <table className="min-w-full w-full divide-y divide-gray-200 border-l border-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('name')}>
                    <div className="flex items-center">Name {getSortIcon('name')}</div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('type')}>
                    <div className="flex items-center">Type {getSortIcon('type')}</div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('surface')}>
                    <div className="flex items-center">Surface {getSortIcon('surface')}</div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('hasLights')}>
                    <div className="flex items-center">Lights {getSortIcon('hasLights')}</div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {getSortedAndFilteredCourts().map((court, idx) => (
                  <tr key={court.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{court.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${court.type === 'tennis' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`}>{court.type}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{court.surface.charAt(0).toUpperCase() + court.surface.slice(1)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${court.hasLights ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>{court.hasLights ? 'Yes' : 'No'}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex space-x-2">
                        <button
                          type="button"
                          className="inline-flex items-center px-3 py-2 border border-indigo-300 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition"
                          onClick={() => setEditingCourt(court)}
                        >
                          <PencilIcon className="h-4 w-4 mr-1" />
                          Edit
                        </button>
                        <button
                          type="button"
                          className="inline-flex items-center px-3 py-2 border border-red-300 text-red-700 bg-red-50 hover:bg-red-100 rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition"
                          onClick={() => handleDeleteCourt(court.id)}
                        >
                          <TrashIcon className="h-4 w-4 mr-1" />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Business Hours Modal */}
      {showBusinessHoursModal && (
        <>
          {/* Overlay */}
          <div className="fixed inset-0 bg-black bg-opacity-40 z-40" />
          <div className="fixed inset-0 z-50 flex items-center justify-center px-2">
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full p-8">
              {/* Close button */}
              <button
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 focus:outline-none"
                onClick={() => setShowBusinessHoursModal(false)}
                aria-label="Close"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <h3 className="text-2xl font-bold text-gray-900 mb-8">Business Hours</h3>
              <div className="space-y-3">
                {DAYS_OF_WEEK.map((day) => {
                  const dayHours = businessHours[day] || defaultBusinessHours[day];
                  return (
                    <div key={day} className="flex items-center mb-3 bg-gray-50 rounded-xl px-4 py-3">
                      <div className="w-28 flex items-center">
                        <input
                          type="checkbox"
                          checked={dayHours.isClosed}
                          onChange={(e) => setBusinessHours(prev => ({
                            ...prev,
                            [day]: { ...dayHours, isClosed: e.target.checked }
                          }))}
                          className="h-5 w-5 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded mr-2"
                        />
                        <span className="text-base font-medium text-gray-700">
                          {day.charAt(0).toUpperCase() + day.slice(1)}
                        </span>
                      </div>
                      {!dayHours.isClosed && (
                        <div className="flex-1 flex items-center gap-4">
                          <div className="flex-1">
                            <label className="block text-xs text-gray-500 mb-1">Open</label>
                            <select
                              className="w-full rounded-md border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 py-2 px-3"
                              value={dayHours.openTime}
                              onChange={(e) => setBusinessHours(prev => ({
                                ...prev,
                                [day]: { ...dayHours, openTime: e.target.value }
                              }))}
                            >
                              {generateTimeOptions().map(time => (
                                <option key={time} value={time}>
                                  {formatTimeForDisplay(time)}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="flex-1">
                            <label className="block text-xs text-gray-500 mb-1">Close</label>
                            <select
                              className="w-full rounded-md border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 py-2 px-3"
                              value={dayHours.closeTime}
                              onChange={(e) => setBusinessHours(prev => ({
                                ...prev,
                                [day]: { ...dayHours, closeTime: e.target.value }
                              }))}
                            >
                              {generateTimeOptions().map(time => (
                                <option key={time} value={time}>
                                  {formatTimeForDisplay(time)}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      )}
                      {dayHours.isClosed && (
                        <div className="flex-1 text-sm text-gray-500 italic ml-4">
                          Closed
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-end gap-3 mt-8">
                <button
                  type="button"
                  className="bg-gray-100 text-gray-700 rounded-md px-6 py-2 font-medium hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  onClick={() => setShowBusinessHoursModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="bg-indigo-600 text-white font-semibold rounded-md px-6 py-2 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  onClick={handleSaveBusinessHours}
                >
                  Save Hours
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Add Court Modal */}
      {showModal && (
        <>
          {/* Overlay */}
          <div className="fixed inset-0 bg-black bg-opacity-40 z-40" />
          <div className="fixed inset-0 z-50 flex items-center justify-center px-2">
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full p-8">
              {/* Close button */}
              <button
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 focus:outline-none"
                onClick={() => setShowModal(false)}
                aria-label="Close"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <h3 className="text-2xl font-bold text-gray-900 mb-8">Add New Court</h3>
              <form onSubmit={handleCreateCourt}>
                <div className="mb-5">
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    className="w-full rounded-md border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 py-2 px-3"
                    value={newCourt.name}
                    onChange={(e) => setNewCourt({ ...newCourt, name: e.target.value })}
                    required
                    placeholder="Enter court name"
                  />
                </div>
                <div className="mb-5">
                  <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">
                    Type
                  </label>
                  <select
                    id="type"
                    className="w-full rounded-md border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 py-2 px-3"
                    value={newCourt.type}
                    onChange={(e) => setNewCourt({ ...newCourt, type: e.target.value as 'tennis' | 'pickleball' })}
                    required
                  >
                    <option value="tennis">Tennis</option>
                    <option value="pickleball">Pickleball</option>
                  </select>
                </div>
                <div className="mb-5">
                  <label htmlFor="surface" className="block text-sm font-medium text-gray-700 mb-1">
                    Surface Type
                  </label>
                  <select
                    id="surface"
                    className="w-full rounded-md border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 py-2 px-3"
                    value={newCourt.surface}
                    onChange={(e) => setNewCourt({ ...newCourt, surface: e.target.value })}
                    required
                  >
                    {surfaceTypes.map((type) => (
                      <option key={type} value={type}>
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-8 flex items-center">
                  <input
                    type="checkbox"
                    className="h-5 w-5 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded mr-2"
                    checked={newCourt.hasLights}
                    onChange={(e) => setNewCourt({ ...newCourt, hasLights: e.target.checked })}
                  />
                  <span className="text-base text-gray-700">Has Lights</span>
                </div>
                <div className="flex justify-end gap-3 mt-8">
                  <button
                    type="button"
                    className="bg-gray-100 text-gray-700 rounded-md px-6 py-2 font-medium hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-indigo-600 text-white font-semibold rounded-md px-6 py-2 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    Create Court
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      {/* Edit Court Modal */}
      {editingCourt && (
        <div className="court-modal">
          <div className="court-modal-content">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Edit Court
                </h3>
                <form onSubmit={handleUpdateCourt}>
                  <div className="court-form-group">
                    <label htmlFor="edit-name" className="court-form-label">
                      Name
                    </label>
                    <input
                      type="text"
                      id="edit-name"
                      className="court-form-input"
                      value={editingCourt.name}
                      onChange={(e) => setEditingCourt({ ...editingCourt, name: e.target.value })}
                      required
                      placeholder="Enter court name"
                    />
                  </div>
                  <div className="court-form-group">
                    <label htmlFor="edit-type" className="court-form-label">
                      Type
                    </label>
                    <select
                      id="edit-type"
                      className="court-form-select"
                      value={editingCourt.type}
                      onChange={(e) => setEditingCourt({ ...editingCourt, type: e.target.value as 'tennis' | 'pickleball' })}
                      required
                    >
                      <option value="tennis">Tennis</option>
                      <option value="pickleball">Pickleball</option>
                    </select>
                  </div>
                  <div className="court-form-group">
                    <label htmlFor="edit-surface" className="court-form-label">
                      Surface Type
                    </label>
                    <select
                      id="edit-surface"
                      className="court-form-select"
                      value={editingCourt.surface}
                      onChange={(e) => setEditingCourt({ ...editingCourt, surface: e.target.value })}
                      required
                    >
                      {surfaceTypes.map((type) => (
                        <option key={type} value={type}>
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="court-form-group">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        className="court-form-checkbox"
                        checked={editingCourt.hasLights}
                        onChange={(e) => setEditingCourt({ ...editingCourt, hasLights: e.target.checked })}
                      />
                      <span className="ml-2 text-sm text-gray-700">Has Lights</span>
                    </label>
                  </div>
                  <div className="court-modal-footer">
                    <button
                      type="button"
                      className="court-modal-button court-modal-button-secondary"
                      onClick={() => setEditingCourt(null)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="court-modal-button court-modal-button-primary"
                    >
                      Update Court
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourtManagement; 